import { reduce, get, noop, isNil } from 'lodash'
import {
  MAP_FORM_PROPS_TO_GOOGLE_PROPS,
  US_TERRITORY_STATE_CODES,
} from '../constants'

const US_TERRITORY_STATE_SET = new Set(US_TERRITORY_STATE_CODES)

/*
 * Normalizes a US-territory address (mapped SelectedAddress shape).
 *
 * For Puerto Rico, Guam, and the US Virgin Islands, Google returns the USPS
 * territory code on the `country` component (-> country_code) and a
 * municipio/island on administrative_area_level_1 (-> country_primary_subdivision).
 * This moves the territory code into country_primary_subdivision (the "state")
 * and sets country_code to 'US', keeping the municipio/island as municipality
 * when Google did not provide a locality. Mainland US and non-territory
 * addresses are returned unchanged.
 *
 * @params address Object a mapped SelectedAddress (post mapPropsFromSourceWithMap)
 */
export const normalizeUsTerritoryAddress = address => {
  const code = (address.country_code || '').toUpperCase()
  if (!US_TERRITORY_STATE_SET.has(code)) {
    return address
  }
  return {
    ...address,
    country_primary_subdivision: code,
    municipality: address.municipality || address.country_primary_subdivision,
    country_code: 'US',
  }
}

/*
 * Return a Map of values we are interested in from the places object
 * @params place Object google places Api results from an address selection
 * @params addressParts Object A map of the address parts we are interested in from place
 */
export const getAddressMapFromPlace = (place, addressParts) => {
  const address = {}
  for (const addressComponent of place.address_components) {
    const addressType = addressComponent.types[0] // several types per address component, first one suffices and matches our addressParts
    const addressComponentValueKey = addressParts[addressType] // short_name vs long_name for key
    if (addressComponentValueKey) {
      address[addressType] = addressComponent[addressComponentValueKey]
    }
  }

  // try to get latitude and longitude
  const lat = get(place, 'geometry.location.lat', noop)()
  const lon = get(place, 'geometry.location.lng', noop)()
  if (!isNil(lat) && !isNil(lon)) {
    address.coordinates = {
      lat,
      lon,
    }
  }

  return address
}

/*
 * Return a map of the google prop values to what the internal Api expects
 * @params source Object the object we want to extract values from
 * @params transformMap Object the map we use to rename the keys in source
 */
export const mapPropsFromSourceWithMap = (source, transformMap) => {
  // Group the values by the key that represents them with the specific order
  // example output : {street_number: [{value: 'value', priority: 0}], municipality: [{value: 'value1', priority: 1}, {value: 'value2', priority: 0}]}
  const groupedValuesWithPriority = reduce(
    source,
    (result, value, key) => {
      const targetKey = transformMap[key]
      if (targetKey) {
        let priority = 0
        if (Array.isArray(MAP_FORM_PROPS_TO_GOOGLE_PROPS[targetKey])) {
          priority = MAP_FORM_PROPS_TO_GOOGLE_PROPS[targetKey].indexOf(key)
        }
        if (!result[targetKey]) {
          result[targetKey] = [{ value, priority }]
        } else {
          result[targetKey].push({ value, priority })
        }
      }
      return result
    },
    {}
  )
  // Reduce the grouped values to the highest priority value only
  // example output : {street_number: 'value', municipality: 'value2'}
  const filteredValuesByType = reduce(
    groupedValuesWithPriority,
    (result, value, key) => {
      const highestPriorityValue = value.sort(
        (a, b) => a.priority - b.priority
      )[0].value
      result[key] = highestPriorityValue
      return result
    },
    {}
  )
  return filteredValuesByType
}
